import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const DISPLAY_STATE = Object.freeze({
  DEFAULT: 'default',
  APPROACHING_EXPIRATION: 'approaching_expiration',
  FINAL_DAY: 'final_day',
});

export const COPY_KEY = Object.freeze({
  ENDS_IN_DAYS: 'endsInDays',
  ENDS_TOMORROW: 'endsTomorrow',
  ENDS_TODAY: 'endsToday',
});

const HIDDEN = Object.freeze({
  isVisible: false,
  copyKey: null,
  displayState: null,
  daysRemaining: null,
  trialEndAt: null,
  promotionalOffer: null,
});

const getPromotionalOffer = (settings) => {
  if (typeof settings?.trialDemoControlOffer !== 'string') {
    return null;
  }
  const promotionalOffer = settings.trialDemoControlOffer.trim();
  return promotionalOffer || null;
};

const getZonedDay = (value, userZone) => {
  if (userZone) {
    try {
      const zoned = dayjs.utc(value).tz(userZone);
      if (zoned.isValid()) {
        return zoned;
      }
    } catch (error) {
      // Unknown IANA zones must not hide the control.
    }
  }
  return dayjs(value);
};

export const getMsUntilNextZonedDay = (now = new Date(), userZone) => {
  const nowDay = getZonedDay(now, userZone);
  if (!nowDay.isValid()) {
    return 60 * 60 * 1000;
  }
  const nextDay = nowDay.add(1, 'day').startOf('day');
  return Math.max(nextDay.diff(nowDay), 60 * 1000);
};

const getDisplayState = (daysRemaining) => {
  if (daysRemaining <= 1) {
    return DISPLAY_STATE.FINAL_DAY;
  }
  if (daysRemaining <= 3) {
    return DISPLAY_STATE.APPROACHING_EXPIRATION;
  }
  return DISPLAY_STATE.DEFAULT;
};

const getCopyKey = (daysRemaining) => {
  if (daysRemaining === 0) {
    return COPY_KEY.ENDS_TODAY;
  }
  if (daysRemaining === 1) {
    return COPY_KEY.ENDS_TOMORROW;
  }
  return COPY_KEY.ENDS_IN_DAYS;
};

/**
 * PCS-11477: visibility + countdown copy for the Toriut app-shell trial control.
 * Owner fields live on `user.team` (same as isToriutTrialExpired).
 */
const getTrialDemoControlState = (user, now = new Date()) => {
  const owner = user?.team || user;
  const trialEnds = owner?.trialEnds;
  const settings = owner?.settings;

  if (owner?.toriutAccessState !== 'trial_active' || !trialEnds || settings?.trialDemoControlEnabled === false) {
    return HIDDEN;
  }

  const userZone = user?.settings?.timeZone?.userTimeZone;
  const nowDay = getZonedDay(now, userZone);
  const endDay = getZonedDay(trialEnds, userZone);

  if (!nowDay.isValid() || !endDay.isValid()) {
    return HIDDEN;
  }

  const daysRemaining = endDay.startOf('day').diff(nowDay.startOf('day'), 'day');
  if (daysRemaining < 0) {
    return HIDDEN;
  }

  return {
    isVisible: true,
    copyKey: getCopyKey(daysRemaining),
    displayState: getDisplayState(daysRemaining),
    daysRemaining,
    trialEndAt: dayjs.utc(trialEnds).toISOString(),
    promotionalOffer: getPromotionalOffer(settings),
  };
};

export default getTrialDemoControlState;
